import moment from "moment-timezone";

export const validateDateFormat = (dateString: string) => {
  const [year, month, day] = dateString.split("-").map(Number);
  const parsedDate = new Date(year, month - 1, day);
  if (
    isNaN(parsedDate.getTime()) ||
    parsedDate.getDate() !== day ||
    parsedDate.getMonth() !== month - 1 ||
    parsedDate.getFullYear() !== year
  ) {
    return {
      success: false,
      message: "Invalid date. Please enter a valid date in YYYY-MM-DD format.",
    };
  }
  return {
    success: true,
    message: "Valid date",
  };
};
export const getFormattedDate = (dateString: string) => {
  const dateObj = new Date(dateString);
  return moment(dateObj).tz("Asia/Kolkata").format("DD/MM/YYYY");
};

export const getFormattedDateWithTime = (dateString: string) => {
  if (!dateString) return "";
  const isoDate = dateString.includes("T")
    ? dateString
    : dateString.replace(" ", "T");
  return (
    moment.tz(isoDate, "Asia/Kolkata").format("DD/MM/YYYY, h:mm A") + " (IST)"
  );
};

/**
 * Format a date to a human-readable string
 * @param date The date to format
 * @returns A formatted date string
 */
export const formatDate = (date: Date): string => {
  // Options for date formatting
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };

  return new Intl.DateTimeFormat("en-US", options).format(date);
};

/**
 * Format a date to a relative time string (e.g., "2 days ago")
 * @param date The date to format
 * @returns A relative time string
 */
export const formatRelativeTime = (date: Date): string => {
  const now = new Date();
  const diffInMilliseconds = now.getTime() - date.getTime();

  // Convert to seconds
  const diffInSeconds = Math.floor(diffInMilliseconds / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds} second${diffInSeconds !== 1 ? "s" : ""} ago`;
  }

  // Convert to minutes
  const diffInMinutes = Math.floor(diffInSeconds / 60);

  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
  }

  // Convert to hours
  const diffInHours = Math.floor(diffInMinutes / 60);

  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
  }

  // Convert to days
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
  }

  // Default to standard date format for older dates
  return formatDate(date);
};

/**
 * Returns the formatted month and 2-digit year for the previous month (current month - 1) in Asia/Kolkata timezone.
 * E.g., if current date is Oct 2026, returns "Sep'26"
 */
export const getPreviousMonthClosingLabel = (): string => {
  return moment()
    .tz("Asia/Kolkata")
    .date(1)
    .subtract(1, "month")
    .format("MMM'YY");
};

/**
 * Returns the title for YoY % Return Trend with previous month closing.
 * E.g. "YoY % Return Trend till Sep'26 Closing" or "${symbol}: YoY % Return Trend till Sep'26 Closing"
 */
export const getYoYReturnTrendTitle = (symbol?: string): string => {
  const previousMonthLabel = getPreviousMonthClosingLabel();
  const baseTitle = `YoY % Return Trend till ${previousMonthLabel} Closing`;
  return symbol ? `${symbol}: ${baseTitle}` : baseTitle;
};
